/**
 * Integrytas Group - Document Management System (DMS)
 * Service: DmsCommonRoutine
 *
 * Centralises client-side validation and storage-path rules for DMS file
 * handling, mirroring the CmsCommonRoutine pattern (Standards v15.0 §3/§5).
 * Server-side authorization and the version chain are enforced by the dms_*
 * SECURITY DEFINER RPCs; these helpers give fast, field-level feedback before
 * a byte ever leaves the browser.
 */

export interface FieldError { field: string; message: string }
export interface ValidationResult { valid: boolean; errors: FieldError[] }

const ok = (): ValidationResult => ({ valid: true, errors: [] });
const fail = (errors: FieldError[]): ValidationResult => ({ valid: false, errors });

// Generic document repository: permit common business document & image types.
// Tune via this list (kept deliberately broad since DMS is application-agnostic).
export const DMS_ALLOWED_TYPES = [
  'application/pdf',
  'text/plain', 'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/jpeg', 'image/png', 'image/webp', 'image/gif',
  'application/zip',
];

export const DMS_MAX_BYTES = 50 * 1024 * 1024; // 50 MB

export function validateDocumentFile(file: File): ValidationResult {
  const errors: FieldError[] = [];
  if (!file || file.size === 0) {
    addError(errors, 'file', 'A non-empty file is required.');
    return fail(errors);
  }
  if (file.size > DMS_MAX_BYTES) {
    addError(errors, 'file', `File exceeds the ${(DMS_MAX_BYTES / 1024 / 1024).toFixed(0)} MB limit (${(file.size / 1024 / 1024).toFixed(1)} MB).`);
  }
  // Empty MIME is allowed (some browsers omit it); only reject KNOWN-bad types.
  if (file.type && !DMS_ALLOWED_TYPES.includes(file.type)) {
    addError(errors, 'file', `File type "${file.type}" is not permitted.`);
  }
  return errors.length ? fail(errors) : ok();
}

function addError(list: FieldError[], field: string, message: string) {
  list.push({ field, message });
}

/** Storage-safe object name from the original filename. */
export function sanitizeFilename(original: string): string {
  return original.toLowerCase().replace(/[^a-z0-9._-]/g, '_').replace(/_+/g, '_').slice(0, 200);
}

/**
 * Build a collision-resistant storage path scoped by target. We cannot know the
 * server-generated document_id before upload, so a random prefix guarantees
 * uniqueness; the path is recorded on the document row by the register/version
 * RPC. Layout: <entity>/<record>/<rand>_<safe-name>
 */
export function buildStoragePath(entity: string, record: string, original: string): string {
  const safeEntity = entity.toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 80);
  const safeRecord = String(record).replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 128);
  const rand =
    (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.round(Math.random() * 1e9)}`);
  return `${safeEntity}/${safeRecord}/${rand}_${sanitizeFilename(original)}`;
}
